// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { fillOutArray } from '../../../../functions/fill-out-array';
import { getBoardYearsFromTimeline } from '../../../../functions/timeline/get-board-years-from-timeline';
import { BoardTimeline } from '../../../../model/board-timeline';
import { CustomTargetModelData } from '../../../../model/custom-model/custom-target-model-data';
import { Dict } from '../../../../model/dict';
import { ClientBoardContainer } from '../../../../public/dashboard/shared/model/client-board-container';
import { convertValuesToGrams } from '../../functions/convert-values-to-grams';
import { getYearValuesFromDict } from '../../functions/get-year-values-from-dict';

@Injectable({
  providedIn: 'root',
})
export class CustomModelDataService {
  constructor() {}
  /**
   * @deprecated should only be used for dashboard v3
   */
  createValidChartDataV3(
    data: CustomTargetModelData,
    board: ClientBoardContainer,
    timeline: BoardTimeline,
    isPanorama = false
  ): CustomTargetModelData {
    const { chartData } = data;
    const years = getBoardYearsFromTimeline(timeline);
    const containsOnlyUnsetValues = (values: (number | undefined)[]) => {
      return values.every((v) => v === undefined);
    };
    const emissionValues = getYearValuesFromDict(chartData.emissions, years);
    const abatementValues = getYearValuesFromDict(chartData.carbonAbatement, years);
    if (containsOnlyUnsetValues(emissionValues)) {
      //Panorama has a lot of custom models, most have only abatement for the end year set.
      //This is a solution to handle that case and that case alone.
      const allButLast = abatementValues.slice(0, -1);
      const last = abatementValues[abatementValues.length - 1];
      if (containsOnlyUnsetValues(allButLast) && last !== undefined) {
        emissionValues[0] = last;
        abatementValues[0] = 0;
      }
    }
    const filledEmissionBau = fillOutArray(convertValuesToGrams(emissionValues, board));
    const filledNetCarbonAbatement = fillOutArray(convertValuesToGrams(abatementValues, board));
    const allTargetValues = getYearValuesFromDict(chartData.target, years);
    //panorama has target values that span the full timeline.
    const filledTargetValues = years.map((year, index) => {
      if (year < timeline.scenarioStartYear && !isPanorama) {
        return undefined;
      }
      const value = allTargetValues[index];
      if (year === timeline.scenarioStartYear && value === undefined && !isPanorama) {
        let filledTargetValues = [];
        if (!containsOnlyUnsetValues(allTargetValues)) {
          filledTargetValues = fillOutArray(allTargetValues);
        }
        return filledTargetValues[index];
      }
      return value;
    });

    const carbonAbatement = years.reduce<Dict<number>>((obj, year, index) => {
      obj[year] =
        chartData.carbonAbatement[year] !== undefined
          ? convertValuesToGrams([chartData.carbonAbatement[year]], board)[0]
          : filledNetCarbonAbatement[index];
      return obj;
    }, {});
    const emissions = years.reduce<Dict<number>>((obj, year, index) => {
      obj[year] =
        chartData.emissions[year] !== undefined
          ? convertValuesToGrams([chartData.emissions[year]], board)[0]
          : filledEmissionBau[index];
      return obj;
    }, {});
    const target = years.reduce<Dict<number>>((obj, year, index) => {
      obj[year] = chartData.target[year] !== undefined ? chartData.target[year] : filledTargetValues[index];
      return obj;
    }, {});
    return {
      ...data,
      chartData: { ...chartData, emissions, carbonAbatement, target },
    };
  }

  /**
   * Converts all data values held in the chart data to grams for abatement and emissions.
   * Fills out the chart data so that it has a valid value for every year in the timeline.
   * The function returns a new object where the ´chartData´ has valid values for emissions, abatement and target.
   * @param data
   * @param board
   * @param timeline
   * @returns
   */
  createValidChartDataV4(
    data: CustomTargetModelData,
    board: ClientBoardContainer,
    timeline: BoardTimeline,
    isPanorama: boolean = false
  ): CustomTargetModelData {
    const { chartData } = data;
    const carbonAbatement = this._createValidCo2Values(timeline, board, chartData.carbonAbatement);
    const emissions = this._createValidCo2Values(timeline, board, chartData.emissions);
    const target = this._createValidTargetValues(timeline, chartData.target, isPanorama);

    return {
      ...data,
      chartData: { ...chartData, emissions, carbonAbatement, target },
    };
  }

  private _containsOnlyUnsetValues(values: (number | undefined)[]) {
    return values.every((v) => v === undefined);
  }

  private _createValidCo2Values(
    timeline: BoardTimeline,
    board: ClientBoardContainer,
    valueDict: Dict<number | undefined>
  ): Dict<number> {
    const years = getBoardYearsFromTimeline(timeline);
    const startYearIndex = years.indexOf(timeline.scenarioStartYear);
    const values = getYearValuesFromDict(valueDict, years);
    return this._createYearDict(
      years,
      fillOutArray(convertValuesToGrams(values, board)).map((value, index) => {
        if (index < startYearIndex) {
          return undefined;
        }
        return value;
      })
    );
  }
  /**
   * Verifies that the target values are valid for the timeline. This is based on how we know the data will be presented, we know that this will be presented as a line series.
   * Therefore it is ok to leave gaps in the data, but we want to have a value for the start year and end year so that the series will stretch across the whole timeline.
   * The exception is if no values has been set at all we'll keep it undefined since we assume that no proper target values exist.
   * @param timeline
   * @param target
   * @returns
   */
  private _createValidTargetValues(
    timeline: BoardTimeline,
    target: Dict<number | undefined>,
    isPanorama: boolean = false
  ): Dict<number | undefined> {
    const years = getBoardYearsFromTimeline(timeline);
    const allTargetValues = getYearValuesFromDict(target, years);
    const filledTargetValues = this._containsOnlyUnsetValues(allTargetValues)
      ? allTargetValues
      : fillOutArray(allTargetValues);
    return this._createYearDict(
      years,
      years.map((year, index) => {
        if (year < timeline.scenarioStartYear && !isPanorama) {
          return undefined;
        }
        if (year === timeline.scenarioStartYear || year === timeline.boardEndYear) {
          return filledTargetValues[index];
        }
        return allTargetValues[index];
      })
    );
  }

  private _createYearDict(years: number[], values: number[]): Dict<number> {
    return years.reduce((obj, year, index) => {
      obj[year] = values[index];
      return obj;
    }, {});
  }
}
